// Диалог «Данные работы»: вид работы и титул, руководитель (запомненные), ключевые слова, студент (профиль),
// практика, состав работы по табл. 1 СТО, раздел об ИИ (п. 3.9) с шаблоном, параметры.
import { useEffect, useRef, useState } from 'react';
import type { DialogProps } from '../../core/registry';
import type { Meta, StudentData } from '../../core/types';
import { MiniEditor } from '../../core/editor/MiniEditor';
import { Btn, Field, Modal } from '../../ui/controls';
import { WORK_TYPES, ELEMENTS, elementNeed, wt, keywordCase, AI_TEMPLATE, INDEP_TEMPLATE, richText, zadanieTitle,
  zadanieTitleOptions, zadanieForm, TASK_FORM_TYPES, tocOn, tocRequired } from './data';
import { TitleView, useProfile } from './title';
import { remember, saveProfile } from './profile';
import { suggestKeywords, applySuggestion } from './keywords';

/** Ключевые слова «фишками»: Enter / запятая - добавить, Backspace - убрать последнее. */
export function KeywordsInput({ value, onChange, testid }: { value: string[]; onChange: (v: string[]) => void; testid?: string }) {
  const [text, setText] = useState('');
  const add = (raw: string) => {
    const parts = raw.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);
    if (parts.length) onChange([...value, ...parts.filter((p) => !value.includes(p))]);
    setText('');
  };
  const n = value.length;
  return (
    <div className="kw-box" onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.focus()}>
      {value.map((k, i) => (
        <span key={k + i} className="kw-chip">{keywordCase(k)}<button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} title="Убрать">×</button></span>
      ))}
      <input value={text} placeholder={n ? '' : 'слово или словосочетание, Enter'} data-testid={testid}
        onChange={(e) => { if (/[,;]/.test(e.target.value)) add(e.target.value); else setText(e.target.value); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); add(text); }
          if (e.key === 'Backspace' && !text && n) onChange(value.slice(0, -1));
        }}
        onBlur={() => text.trim() && add(text)}
        onPaste={(e) => { const t = e.clipboardData.getData('text'); if (/[,;\n]/.test(t)) { e.preventDefault(); add(text + t); } }} />
      <span className={'kw-count ' + (n >= 5 && n <= 15 ? 'good' : 'bad')}>{n} / 5–15</span>
    </div>
  );
}

const STUDENT_FIELDS: [keyof StudentData, string, boolean][] = [
  ['student', 'Ф. И. О. полностью', true], ['student_short', 'Инициалы и фамилия (подпись)', false], ['group', 'Группа', false],
  ['coauthors', 'Соавторы (групповая работа): Ф. И. О. через запятую', true],
  ['course', 'Курс', false], ['direction_code', 'Код направления', false], ['direction_name', 'Направление подготовки', true],
  ['university', 'Университет', true], ['branch', 'Филиал', true], ['institute', 'Институт / высшая школа', true],
  ['city', 'Город', false], ['year', 'Год', false],
];

export function MetaDialog({ ctx, props, close }: DialogProps) {
  const initial = ctx.store.getState().meta!;
  const [m, setM] = useState<Meta>(JSON.parse(JSON.stringify(initial)));
  const [tab, setTab] = useState<string>(props.tab || 'work');
  const [toProfile, setToProfile] = useState(false);
  const [tplVer, setTplVer] = useState(0);
  const [kwBusy, setKwBusy] = useState(false);
  // «Подобрать по тексту»: пустой или подобранный ранее список заменяется, заданный автором - дополняется
  const pickKeywords = async () => {
    setKwBusy(true);
    try {
      const r = await suggestKeywords(ctx, m);
      const res = applySuggestion(m, r);
      if (!res.added) ctx.toast(r.keywords.length || r.front?.length ? 'Новых ключевых слов по тексту не найдено'
        : 'По тексту работы надёжно подобрать ключевые слова не удалось - впишите 5–15 слов сами', 'warn', 6000);
      else setM(res.meta);
    } catch (e) {
      ctx.toast('Не удалось подобрать ключевые слова: ' + String((e as Error)?.message || e), 'error');
    } finally { setKwBusy(false); }
  };
  const prof = useProfile();
  const body = useRef<HTMLDivElement>(null);
  const set = (patch: Partial<Meta>) => setM((cur) => ({ ...cur, ...patch }));
  // правка поля, заполненного сайтом при загрузке (meta.auto: тема, ФИО из профиля), снимает отметку «проверьте»
  const unAuto = (cur: Meta, k: string) => (cur.auto || []).filter((x) => x !== k);
  const setTitle = (title: string) => setM((cur) => ({ ...cur, title, title_auto: false, auto: unAuto(cur, 'title') }));
  const setSt = (k: string, v: string) => setM((cur) => ({ ...cur, student: { ...cur.student, [k]: v },
    ...(k === 'student' ? { auto: unAuto(cur, 'student') } : {}) }));
  const isAuto = (k: string) => (m.auto || []).includes(k);
  useEffect(() => {
    if (!props.field) return;
    const el = body.current?.querySelector(`[data-field="${props.field}"]`) as HTMLElement | null;
    el?.focus();
  }, [tab]);
  const apply = async () => {
    ctx.store.getState().setMeta(() => m);
    void remember(m.teacher.name ? m.teacher : null, m.discipline);
    if (toProfile) {
      // соавторы групповой работы в профиль (данные по умолчанию для новых отчётов) не сохраняются
      const { coauthors: _co, ...stud } = m.student;
      try { await saveProfile({ student: stud as StudentData }); ctx.toast('Данные студента сохранены в профиль', 'ok'); } catch { /* ok */ }
    }
    close(true);
  };
  const tabs: [string, string][] = [['work', 'Работа и титул'], ['student', 'Студент'], ['front', 'Состав работы'], ['ai', 'Раздел об ИИ'], ['options', 'Параметры']];
  if (m.type === 'practice') tabs.splice(2, 0, ['practice', 'Практика']);
  const w = wt(m.type);
  return (
    <Modal title="Данные работы" onClose={() => close()} width={1040} testid="meta-dialog" footer={<>
      <span className="muted small">Изменения попадут на титул, в реферат и свойства PDF</span>
      <span className="topbar-spacer" />
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Готово" primary onClick={apply} testid="meta-ok" />
    </>}>
      <div className="meta-layout">
        <div className="meta-main" ref={body}>
          <div className="tabs">
            {tabs.map(([id, t]) => <button key={id} type="button" className={'tab' + (tab === id ? ' is-active' : '')} onClick={() => setTab(id)} data-testid={'meta-tab-' + id}>{t}</button>)}
          </div>
          {tab === 'work' && (
            <div className="form-grid">
              <Field label="Вид работы" wide>
                <select value={m.type} onChange={(e) => set({ type: e.target.value as Meta['type'] })} data-testid="meta-type">
                  {WORK_TYPES.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </Field>
              {m.type !== 'practice' && <Field label="Номер работы"><input value={m.number} onChange={(e) => set({ number: e.target.value })} data-field="number" data-testid="meta-number" /></Field>}
              <Field label="Вариант" hint="Пусто - строки «Вариант №» на титуле не будет"><input value={m.variant} onChange={(e) => set({ variant: e.target.value })} data-field="variant" /></Field>
              {m.type !== 'practice' && (
                <Field label="Дисциплина" wide>
                  <input list="sto-disciplines" value={m.discipline} onChange={(e) => set({ discipline: e.target.value })} data-field="discipline" data-testid="meta-discipline" />
                  <datalist id="sto-disciplines">{(prof?.disciplines || []).map((d) => <option key={d} value={d} />)}</datalist>
                </Field>
              )}
              <Field label="Тема работы" wide hint={isAuto('title') ? 'Взята при загрузке не с титула (из текста, заголовка или имени файла) - проверьте'
                : 'С прописной буквы, без точки и кавычек'}>
                <textarea rows={2} value={m.title} onChange={(e) => setTitle(e.target.value.replace(/\n/g, ' '))} data-field="title" data-testid="meta-title"
                  className={isAuto('title') ? 'meta-auto' : undefined} />
              </Field>
              <Field label="Руководитель (Ф. И. О. полностью)" hint={isAuto('teacher') ? 'На титуле загруженного файла ФИО с опечаткой или инициалами - взято из списка руководителей профиля; проверьте' : undefined}>
                <input list="sto-teachers" value={m.teacher.name} data-field="teacher" data-testid="meta-teacher"
                  className={isAuto('teacher') ? 'meta-auto' : undefined}
                  onChange={(e) => {
                    const name = e.target.value;
                    const known = prof?.teachers.find((x) => x.name === name);
                    setM((cur) => ({ ...cur, teacher: { name, position: known && !cur.teacher.position ? known.position : cur.teacher.position },
                      auto: unAuto(cur, 'teacher') }));
                  }} />
                <datalist id="sto-teachers">{(prof?.teachers || []).map((x) => <option key={x.name} value={x.name}>{x.position}</option>)}</datalist>
              </Field>
              <Field label="Должность, степень, звание" hint="Только если точно известно (иначе оставьте пустым)">
                <input list="sto-positions" value={m.teacher.position} onChange={(e) => set({ teacher: { ...m.teacher, position: e.target.value } })} />
                <datalist id="sto-positions">{['доцент кафедры', 'ст. преподаватель', 'канд. техн. наук, доцент', 'д-р техн. наук, профессор', 'ассистент']
                  .map((x) => <option key={x} value={x} />)}</datalist>
              </Field>
              <Field label="Ключевые слова (5–15)" wide hint={m.keywords_auto
                ? 'Подобраны автоматически по тексту работы - проверьте: уберите лишние, допишите недостающие (3.2.6)'
                : 'В реферат (3.2.6) и в свойства PDF (3.12.2); строчными, кроме аббревиатур'}>
                <div data-field="keywords" tabIndex={-1} className={m.keywords_auto ? 'kw-auto' : undefined}>
                  <KeywordsInput value={m.keywords} onChange={(keywords) => set({ keywords, keywords_auto: false })} testid="meta-keywords" />
                </div>
                <span className="panel-tools">
                  <Btn label={kwBusy ? 'Подбор…' : 'Подобрать по тексту'} disabled={kwBusy} onClick={() => { void pickKeywords(); }}
                    title="Подобрать ключевые слова по теме, заголовкам и тексту работы" testid="meta-keywords-suggest" />
                  {m.keywords_auto && <Btn label="Проверено" onClick={() => set({ keywords_auto: false })}
                    title="Ключевые слова проверены - снять пометку «подобраны автоматически»" testid="meta-keywords-ok" />}
                </span>
              </Field>
            </div>
          )}
          {tab === 'student' && (<>
            <div className="form-grid">
              {STUDENT_FIELDS.map(([k, label, wide]) => (
                <Field key={k} label={label} wide={wide} hint={k === 'student' && isAuto('student') ? 'На титуле загруженного файла ФИО нет - взято из профиля; проверьте' : undefined}>
                  <input value={(m.student as any)[k] || ''} onChange={(e) => setSt(k, e.target.value)} data-field={k} data-testid={'meta-st-' + k}
                    className={k === 'student' && isAuto('student') ? 'meta-auto' : undefined} /></Field>
              ))}
            </div>
            <div className="panel-tools">
              <label className="check small"><input type="checkbox" checked={toProfile} onChange={(e) => setToProfile(e.target.checked)} /> сохранить как данные по умолчанию для новых отчётов</label>
              {/* соавторы - данные этой работы, в профиле их нет: при подстановке профиля остаются */}
              {prof && <Btn label="Взять из профиля" onClick={() => set({ student: { ...prof.student, coauthors: m.student.coauthors } })} />}
            </div>
          </>)}
          {tab === 'practice' && (
            <div className="form-grid">
              {([['kind', 'Вид практики', 'учебная / производственная'], ['type', 'Тип практики', 'ознакомительная, технологическая…'],
                ['place', 'Место прохождения (база практики)', 'АО «ПО «Севмаш»'], ['from', 'Срок: с', '«01» июля 2026 г.'], ['to', 'по', '«28» июля 2026 г.']] as [string, string, string][])
                .map(([k, label, ph]) => (
                  <Field key={k} label={label} wide={k === 'place' || k === 'type'}>
                    <input list={k === 'kind' ? 'sto-pr-kinds' : undefined} value={(m.practice as any)[k] || ''} placeholder={ph}
                      onChange={(e) => set({ practice: { ...m.practice, [k]: e.target.value } })} />
                  </Field>
                ))}
              <datalist id="sto-pr-kinds"><option value="учебная" /><option value="производственная" /></datalist>
            </div>
          )}
          {tab === 'front' && (
            <div className="front-edit">
              <p className="muted small">Состав работы по табл. 1 СТО для вида «{w.name}»: <b>+</b> - обязательно, <b>р</b> - рекомендуется.
                Порядок листов ставится автоматически. Тексты задания и реферата пишутся прямо на листах над отчётом.</p>
              <div className="elements">
                {ELEMENTS.filter((el) => el.id !== 'intro' && el.id !== 'concl' && el.id !== 'sources').map((el) => {
                  const need = elementNeed(el, m.type);
                  const on = el.id === 'zadanie' ? m.zadanie.enabled : el.id === 'remarks' ? m.remarks_page : el.id === 'referat' ? (m.referat.enabled || m.annotation.enabled)
                    : el.id === 'ai' ? m.ai_usage.enabled : el.id === 'indep' ? !!m.indep?.enabled : el.id === 'toc' ? tocOn(m) : true;
                  const toggle = (v: boolean) => {
                    if (el.id === 'zadanie') set({ zadanie: { ...m.zadanie, enabled: v } });
                    if (el.id === 'remarks') set({ remarks_page: v });
                    if (el.id === 'toc') set({ toc_page: v });
                    if (el.id === 'referat') set({ referat: { ...m.referat, enabled: v }, annotation: { ...m.annotation, enabled: false } });
                    if (el.id === 'ai') set({ ai_usage: { ...m.ai_usage, enabled: v } });
                    if (el.id === 'indep') set({ indep: { doc: m.indep?.doc || { type: 'doc', content: [{ type: 'paragraph' }] }, enabled: v } });
                  };
                  return (
                    <label key={el.id} className={'element-row' + (on ? ' is-on' : '')}>
                      <input type="checkbox" checked={on} disabled={el.id === 'toc' && tocRequired(m)} onChange={(e) => toggle(e.target.checked)} data-testid={'meta-el-' + el.id} />
                      <span className={'need need-' + (need === '+' ? 'req' : need === 'р' ? 'rec' : 'opt')}>{need || '–'}</span>
                      <span className="element-title">{el.title}</span>
                      <span className="muted small">п. {el.rule}{el.note ? ' · ' + el.note : ''}</span>
                    </label>
                  );
                })}
              </div>
              {m.zadanie.enabled && (
                <Field label="Заголовок задания">
                  <select value={zadanieTitle(m)} onChange={(e) => set({ zadanie: { ...m.zadanie, title: e.target.value } })}>
                    {zadanieTitleOptions(m).map((t) => <option key={t}>{t}</option>)}
                  </select>
                  {TASK_FORM_TYPES.includes(m.type) && (
                    <label className="check small"><input type="checkbox" checked={zadanieForm(m)} data-testid="meta-zadanie-form"
                      onChange={(e) => set({ zadanie: { ...m.zadanie, form: e.target.checked } })} /> лист задания бланком по форме Прил. Б
                      (снимите - будет заголовок и текст, как набраны)</label>
                  )}
                </Field>
              )}
              {m.indep?.enabled && (
                <Field label="Сведения о самостоятельности выполнения работы (2.16, образец - Прил. А): лист выводится последним, после приложений" wide>
                  {!richText(m.indep.doc).trim() && (
                    <Btn icon="template" label="Заполнить по образцу" onClick={() => { setTplVer((x) => x + 1); set({ indep: { enabled: true,
                      doc: { type: 'doc', content: INDEP_TEMPLATE(m.title).map((t) => ({ type: 'paragraph', content: [{ type: 'text', text: t }] })) } } }); }}
                      testid="indep-template" />
                  )}
                  <MiniEditor key={'i' + tplVer} where="Сведения о самостоятельности" value={m.indep.doc}
                    onChange={(doc) => setM((cur) => ({ ...cur, indep: { enabled: true, doc } }))} />
                </Field>
              )}
              {(m.referat.enabled || m.annotation.enabled) && (
                <div className="radio-row">
                  <label className="check"><input type="radio" checked={m.referat.enabled} onChange={() => set({ referat: { ...m.referat, enabled: true }, annotation: { ...m.annotation, enabled: false } })} /> Реферат (3.2.3: сведения об объёме, ключевые слова, текст)</label>
                  <label className="check"><input type="radio" checked={m.annotation.enabled} onChange={() => set({ annotation: { ...m.annotation, enabled: true }, referat: { ...m.referat, enabled: false } })} /> Аннотация</label>
                </div>
              )}
            </div>
          )}
          {tab === 'ai' && (
            <div className="front-edit">
              <label className="check"><input type="checkbox" checked={m.ai_usage.enabled} onChange={(e) => set({ ai_usage: { ...m.ai_usage, enabled: e.target.checked } })} data-testid="meta-ai" />
                {' '}Включить раздел «Информация об использовании технологий искусственного интеллекта (ИИ)» (п. 3.9)</label>
              <p className="muted small">Раздел нужен, только если при подготовке работы применялся ИИ. Он ставится отдельным разделом перед
                «Выводами/Заключением» и нумеруется автоматически; состоит из фактологического описания и резюме-рефлексии.</p>
              {m.ai_usage.enabled && <>
                {!richText(m.ai_usage.facts).trim() && !richText(m.ai_usage.reflection).trim() && (
                  <Btn icon="template" label="Заполнить шаблоном" onClick={() => { setTplVer((x) => x + 1); set({ ai_usage: { ...m.ai_usage,
                    facts: { type: 'doc', content: AI_TEMPLATE.facts.map((t) => ({ type: 'paragraph', content: [{ type: 'text', text: t }] })) },
                    reflection: { type: 'doc', content: AI_TEMPLATE.reflection.map((t) => ({ type: 'paragraph', content: [{ type: 'text', text: t }] })) } } }); }}
                    testid="ai-template" />
                )}
                <Field label="Фактологическое описание: какие системы ИИ, для чего, как проверялись результаты" wide>
                  <MiniEditor key={'f' + tplVer} where="Раздел об ИИ" value={m.ai_usage.facts} onChange={(facts) => setM((cur) => ({ ...cur, ai_usage: { ...cur.ai_usage, facts } }))} />
                </Field>
                <Field label="Резюме-рефлексия: что дало применение ИИ, ограничения, личный вклад" wide>
                  <MiniEditor key={'r' + tplVer} where="Раздел об ИИ" value={m.ai_usage.reflection} onChange={(reflection) => setM((cur) => ({ ...cur, ai_usage: { ...cur.ai_usage, reflection } }))} />
                </Field>
              </>}
            </div>
          )}
          {tab === 'options' && (
            <div className="form-grid">
              <Field label="Порядок списка источников" wide>
                <select value={m.options.sources_order} onChange={(e) => set({ options: { ...m.options, sources_order: e.target.value as any } })}>
                  <option value="alpha">По алфавиту, иностранные в конце (4.13.4)</option>
                  <option value="as-is">Как в библиотеке источников</option>
                </select>
              </Field>
              <Field label="Знаки в конце позиций перечислений" wide>
                <select value={m.options.list_punct} onChange={(e) => set({ options: { ...m.options, list_punct: e.target.value as any } })}>
                  <option value="warn">Подсказывать при наборе</option>
                  <option value="fix">Ставить «;» и «.» автоматически при экспорте</option>
                </select>
              </Field>
            </div>
          )}
        </div>
        <div className="meta-preview" title="Предпросмотр титульного листа">
          <div className="meta-preview-page"><TitleView m={m} compact /></div>
        </div>
      </div>
    </Modal>
  );
}

